/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Checkbox, Group, Loader, Select, Space, Text } from '@mantine/core';
import { i18n } from '@lingui/core';
import { FC, useContext, useEffect, useState } from 'react';
import { IconChevronDown, IconTemplate } from '@tabler/icons';
import { useParams } from 'react-router-dom';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { useRecommendationMail } from '../../../../Service/hooks/useRecommendationMail';
import { AgencyTemplateDTO } from '../../../../Types/MessageT';
import { Contact } from '../../../../Service/ContactS';
import { EventBaseInput, EventRichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { DataS } from '../../../../Service/DataS';
import { RecommendationDto } from '../../../../Types/RecommendationT';
import { EventContext } from '../../../../Service/Context/EventContext';
import { StringUtils } from '../../../../Utils/utils';
import { EventS } from '../../../../Service/EventS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import AttachmentsAlert from './AttachmentsAlert';
import { TiptapS } from '../../../../Service/TiptapS';

interface Props {
  removeRecommendation: (recommendationId: string) => void;
  recommendation: RecommendationDto;
  contactEmail: string | null | undefined;
  recommendedContact: Contact;
  closeModal: () => void;
}

const RecommendationDeleteModal: FC<Props> = ({
  recommendation,
  removeRecommendation,
  contactEmail,
  closeModal,
  recommendedContact,
}) => {
  const { id: recommendationId } = recommendation;
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const { profileId } = useJwtClaims();
  const { id: eventId } = useParams();
  const { dates } = useContext<any>(EventContext);
  const recommendationDates = RecommendationS.extractRecommendationDates(dates, recommendation);
  const { templates } = useAgencyTemplates();
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const [showAttachmentsAlert, setShowAttachmentsAlert] = useState(false);

  const { subject, message, updateProp, lastUpdate } = useRecommendationMail(
    'CANCELLATION',
    recommendation,
    selectedTemplate,
  );

  useEffect(() => {
    if (selectedTemplate) {
      if (selectedTemplate.attachments.length > 0) {
        setShowAttachmentsAlert(true);
      } else {
        setShowAttachmentsAlert(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const onSubmit = async () => {
    setLoading(true);
    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId: Number(eventId),
      message,
      subject,
    });
    if (placeholdersResolved) {
      if (recommendationId) {
        const deleted = await RecommendationS.remove(recommendationId, sendEmail, {
          message,
          subject,
          newVersion: true,
        });

        if (deleted) {
          removeRecommendation(recommendationId);
          closeModal();
        }
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Text mb="xl">{i18n._('event.recommendations.delete.confirm')}</Text>
      <Text>Soll der Dienstleister darüber per E-Mail benachrichtigt werden?</Text>
      <Checkbox
        size="xs"
        my="md"
        label="Ja, Dienstleister per E-Mail benachrichtigen"
        disabled={loading || !contactEmail}
        checked={sendEmail}
        onChange={() => setSendEmail(!sendEmail)}
      />
      {sendEmail && (
        <>
          <Select
            mb="xs"
            label={
              <Text size="xs" color="dimmed">
                Vorlage
              </Text>
            }
            nothingFound="Keine Vorlagen"
            icon={<IconTemplate size={20} />}
            data={DataS.getAgencyTemplateOptions(templates)}
            value={selectedTemplate ? selectedTemplate.id : undefined}
            onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            rightSection={<IconChevronDown size={14} />}
            rightSectionWidth={25}
          />
          {showAttachmentsAlert && <AttachmentsAlert />}
          <EventBaseInput
            updateTrigger={lastUpdate}
            toolbar={<BaseToolbar />}
            label="Betreff"
            value={subject}
            onUpdate={(html) => updateProp({ subject: html })}
          />
          <Space h="sm" />

          <Text mb="sm">{`Hey ${RecommendationS.getGreeting(recommendedContact)},`} </Text>
          <Text mb="sm">
            {`die Anfrage für das Event am ${StringUtils.concatStrings(
              recommendationDates.map(EventS.formatEventDate),
            )} hat sich erledigt und Du kannst den Termin wieder freigeben.
            Trotzdem Danke für's freihalten!`}
          </Text>
          <EventRichInput
            updateTrigger={lastUpdate}
            isEmailInput
            styles={TiptapS.TextAreaStyles}
            label="Nachricht an den Dienstleister"
            value={message}
            onUpdate={(html) => updateProp({ message: html })}
            toolbar={
              <TemplateMessageToolbar
                saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments: [] } })}
              />
            }
          />
          <Text mt="xs" size="xs" color="dimmed">
            Hinweis: Diese Nachricht wird zur Zeit noch nicht im System gespeichert - Du kannst Sie also im nicht noch
            einmal einsehen.
          </Text>
        </>
      )}
      <Group position="right" mt="xl">
        <Button color="gray" variant="subtle" onClick={closeModal}>
          {i18n._('actions.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          color="red"
          variant="subtle"
          sx={{ backgroundColor: '#FFEFEF' }}
          type="submit"
          disabled={loading}
          leftIcon={loading && <Loader size="xs" />}
        >
          {i18n._('Vermittlung löschen')}
        </Button>
      </Group>
    </>
  );
};

export default RecommendationDeleteModal;
